<template>
  <v-container fluid class="dashboard-container pa-0 pb-12">
    <!-- Header: Identity & Critical Pulse -->
    <v-sheet color="white" class="pa-6 pa-md-8 mb-6 border-bottom shadow-xs">
      <v-row align="center" justify="space-between">
        <v-col cols="12" lg="6">
          <div class="d-flex align-center">
            <v-avatar size="52" color="primary lighten-5" class="mr-4 rounded-xl">
              <v-icon color="primary">fas fa-file-invoice-dollar</v-icon>
            </v-avatar>
            <div>
              <div class="d-flex align-center flex-wrap" style="gap: 8px">
                <h1 class="text-h5 font-weight-black primary--text tracking-tight mb-0">
                  Capital por Facturar
                </h1>
                <v-chip v-if="reportDate" x-small label color="blue-grey lighten-5" class="blue-grey--text text--darken-3 font-weight-medium">
                  <v-icon left x-small>fas fa-clock</v-icon>
                  Actualizado {{ reportDate }}
                </v-chip>
              </div>
              <p class="body-2 grey--text text--darken-1 font-weight-medium mb-0 mt-1">
                Control de Órdenes de Trabajo terminadas en taller pendientes de emisión DTE · Período:
                <span class="font-weight-bold grey--text text--darken-3">{{ periodLabel }}</span>
              </p>
            </div>
          </div>
        </v-col>

        <!-- Critical Pulse Ticker & Action Buttons -->
        <v-col cols="12" lg="6" class="d-flex align-center justify-lg-end flex-wrap" style="gap: 12px">
          <!-- Daily Critical Pulse Card -->
          <div v-if="agingBuckets && agingBuckets.critical" class="daily-pulse-card d-flex align-center px-4 py-2 rounded-xl">
            <div class="pulse-indicator is-critical mr-3"></div>
            <div>
              <div class="caption font-weight-bold text-uppercase error--text text--darken-2">Pulso Crítico (>10d)</div>
              <div class="d-flex align-baseline" style="gap: 6px">
                <span class="font-weight-black text-body-1 grey--text text--darken-4">
                  {{ agingBuckets.critical.net_fmt }}
                </span>
                <span class="caption font-weight-bold error--text">
                  {{ agingBuckets.critical.count }} OTs en mora
                </span>
              </div>
            </div>
          </div>

          <v-btn
            outlined
            color="primary"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            :loading="downloadingPdf"
            @click="downloadExecutivePdf"
          >
            <v-icon left small color="error">fas fa-file-pdf</v-icon>
            Exportar PDF
          </v-btn>

          <v-btn
            outlined
            color="success"
            class="rounded-xl px-4 font-weight-bold text-none shadow-xs"
            :loading="downloading"
            @click="downloadExcel"
          >
            <v-icon left small color="success">fas fa-file-excel</v-icon>
            Exportar Excel
          </v-btn>

          <v-btn
            color="primary"
            depressed
            class="rounded-xl px-5 font-weight-bold text-none shadow-xs"
            :loading="loading"
            @click="loadDashboard"
          >
            <v-icon left small>fas fa-sync-alt</v-icon>
            Actualizar
          </v-btn>
        </v-col>
      </v-row>
    </v-sheet>

    <div class="px-6 px-md-8">
      <!-- Time Control & Quick Presets Bar -->
      <v-card flat outlined class="filter-card pa-4 mb-6 shadow-xs">
        <v-row align="center" dense>
          <v-col cols="12" md="7">
            <div class="d-flex align-center flex-wrap" style="gap: 6px">
              <span class="caption font-weight-bold grey--text text--darken-2 mr-2 text-uppercase">
                Rango rápido:
              </span>
              <v-btn
                v-for="preset in presets"
                :key="preset.id"
                x-small
                depressed
                :color="activePreset === preset.id ? 'primary' : 'grey lighten-4'"
                :class="activePreset === preset.id ? 'white--text font-weight-bold' : 'grey--text text--darken-3 font-weight-medium'"
                class="rounded-lg text-none px-3 py-2"
                @click="applyPreset(preset)"
              >
                {{ preset.label }}
              </v-btn>
            </div>
          </v-col>

          <v-col cols="12" md="5" class="d-flex align-center justify-md-end flex-wrap" style="gap: 8px">
            <div class="d-flex align-center" style="gap: 8px">
              <v-text-field
                v-model="startDate"
                label="Terminadas desde"
                outlined
                dense
                hide-details
                type="date"
                class="compact-date-input"
                @change="onManualDateChange"
              />
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Loading Skeletons -->
      <v-row v-if="loading && !reportDate" dense class="mb-6">
        <v-col v-for="i in 4" :key="'sk-' + i" cols="12" sm="6" md="3">
          <v-skeleton-loader type="card" height="136" class="rounded-xl shadow-xs" />
        </v-col>
      </v-row>

      <template v-else>
        <!-- Hero KPIs: 4 Cards -->
        <v-row dense class="mb-6">
          <!-- KPI 1: Capital Retenido Total (North Star) -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100 is-primary">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Capital Retenido Total</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Monto neto acumulado de OTs finalizadas en taller pendientes de emisión de factura o guía.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value primary--text text--darken-3">
                  {{ totalNetFmt || '$0' }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      {{ totalCount }} OTs finalizadas
                    </span>
                    <span class="caption font-weight-bold grey--text text--darken-2">
                      Bruto: {{ totalGrossFmt || '$0' }}
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 2: Promedio Días sin Facturar -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Promedio Días en Mora</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Días promedio transcurridos desde que la OT se marcó como terminada en taller hasta hoy.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value warning--text text--darken-3">
                  {{ avgDaysPending }} <span class="unit-text">días</span>
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1">
                    Desde término físico en planta
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 3: Índice IEF Global -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Índice IEF Global</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Índice de Eficiencia de Facturación: Porcentaje de volumen emitido dentro de la ventana óptima (≤ 3 días).</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value" :class="getIefClass(overallIef)">
                  {{ overallIef }}%
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold grey--text text--darken-1">Estándar: &le; 3 días</span>
                    <span class="caption font-weight-bold" :class="overallIef >= 80 ? 'success--text text--darken-2' : 'warning--text text--darken-2'">
                      {{ overallIef >= 80 ? 'Saludable' : 'Oportunidad' }}
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- KPI 4: Capital Crítico (>10d) -->
          <v-col cols="12" sm="6" md="3">
            <v-card flat class="hero-kpi-card h-100">
              <v-card-text class="pa-5">
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="kpi-label">Capital en Mora Crítica</div>
                  <v-tooltip bottom max-width="260">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" x-small color="grey">fas fa-info-circle</v-icon>
                    </template>
                    <span>Monto neto en OTs con más de 10 días desde su finalización sin haber sido facturadas.</span>
                  </v-tooltip>
                </div>
                <div class="kpi-value error--text text--darken-2">
                  {{ agingBuckets ? agingBuckets.critical.net_fmt : '$0' }}
                </div>
                <div class="kpi-footer mt-3 pt-3">
                  <div class="d-flex align-center justify-space-between">
                    <span class="caption font-weight-bold error--text">
                      {{ agingBuckets ? agingBuckets.critical.count : 0 }} OTs (>10 días)
                    </span>
                    <span class="caption font-weight-bold grey--text text--darken-1">
                      {{ agingBuckets ? agingBuckets.critical.pct : 0 }}% del total
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Panel Matriz de Envejecimiento (Semáforo) -->
        <v-card v-if="agingBuckets" class="panel-card mb-6 shadow-xs" flat>
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="panel-title">Matriz de Tiempos de Facturación (Antigüedad de OTs)</div>
            <span class="caption grey--text font-weight-bold">Distribución por ventana de mora</span>
          </div>
          <div class="panel-sub mb-4">
            Monitoreo preventivo del ciclo de cobro: desde término técnico en taller hasta emisión de documento tributario
          </div>

          <!-- Barra Multi-Segmento Apilada -->
          <div class="margin-stacked-track mb-5">
            <div
              class="stacked-segment is-healthy"
              :style="{ width: `${agingBuckets.normal.pct || 0}%` }"
              :title="`Normal: ${agingBuckets.normal.net_fmt} (${agingBuckets.normal.pct}%)`"
            >
              <span v-if="agingBuckets.normal.pct >= 8" class="segment-label">
                {{ agingBuckets.normal.pct }}%
              </span>
            </div>
            <div
              class="stacked-segment is-tight"
              :style="{ width: `${agingBuckets.attention.pct || 0}%` }"
              :title="`Atención: ${agingBuckets.attention.net_fmt} (${agingBuckets.attention.pct}%)`"
            >
              <span v-if="agingBuckets.attention.pct >= 8" class="segment-label">
                {{ agingBuckets.attention.pct }}%
              </span>
            </div>
            <div
              class="stacked-segment is-loss"
              :style="{ width: `${agingBuckets.critical.pct || 0}%` }"
              :title="`Crítico: ${agingBuckets.critical.net_fmt} (${agingBuckets.critical.pct}%)`"
            >
              <span v-if="agingBuckets.critical.pct >= 8" class="segment-label">
                {{ agingBuckets.critical.pct }}%
              </span>
            </div>
          </div>

          <!-- 3 Tarjetas de Estado Semáforo -->
          <v-row dense>
            <v-col cols="12" md="4">
              <div class="margin-tier-btn is-healthy">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name d-flex align-center">
                    <span class="swatch" style="background: #10b981"></span> Normal (0-3 días)
                  </span>
                  <span class="tier-count">{{ agingBuckets.normal.count }} OTs</span>
                </div>
                <div class="tier-sales">{{ agingBuckets.normal.net_fmt }}</div>
                <div class="tier-share">{{ agingBuckets.normal.pct }}% del volumen pendiente</div>
              </div>
            </v-col>

            <v-col cols="12" md="4">
              <div class="margin-tier-btn is-tight">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name d-flex align-center">
                    <span class="swatch" style="background: #f59e0b"></span> Atención (4-10 días)
                  </span>
                  <span class="tier-count">{{ agingBuckets.attention.count }} OTs</span>
                </div>
                <div class="tier-sales">{{ agingBuckets.attention.net_fmt }}</div>
                <div class="tier-share">{{ agingBuckets.attention.pct }}% del volumen pendiente</div>
              </div>
            </v-col>

            <v-col cols="12" md="4">
              <div class="margin-tier-btn is-loss">
                <div class="d-flex align-center justify-space-between">
                  <span class="tier-name d-flex align-center">
                    <span class="swatch" style="background: #e11d48"></span> Crítico (>10 días)
                  </span>
                  <span class="tier-count">{{ agingBuckets.critical.count }} OTs</span>
                </div>
                <div class="tier-sales">{{ agingBuckets.critical.net_fmt }}</div>
                <div class="tier-share">{{ agingBuckets.critical.pct }}% del volumen pendiente</div>
              </div>
            </v-col>
          </v-row>
        </v-card>

        <!-- Tactical Insight Banner (Midnight Slate Theme) -->
        <v-card v-if="tacticalInsight" color="#0F172A" dark flat class="rounded-xl pa-6 mb-6 border-top-accent elevation-2 text-left">
          <div class="d-flex align-center mb-3 justify-start">
            <v-chip color="success" x-small class="font-weight-black text-uppercase tracking-widest px-3 mr-3">
              INSIGHT TÁCTICO DEL DÍA
            </v-chip>
            <div class="text-subtitle-1 font-weight-black yellow--text text--accent-2">{{ tacticalInsight.title }}</div>
          </div>
          <div class="text-body-1 grey--text text--lighten-2 mb-4 text-left lh-16" v-html="tacticalInsight.body"></div>
          <div v-if="tacticalInsight.highlight" class="text-left">
            <v-chip color="yellow darken-1" text-color="black" small class="font-weight-black text-uppercase px-3">
              {{ tacticalInsight.highlight }}
            </v-chip>
          </div>
        </v-card>

        <!-- DataTables Tabs Section -->
        <v-card flat class="panel-card mb-8 shadow-xs">
          <v-tabs v-model="activeTab" background-color="grey lighten-4" active-class="white primary--text font-weight-black elevation-1" class="rounded-t-lg" grow>
            <v-tab class="font-weight-bold text-uppercase tracking-wide">
              <v-icon left small>fas fa-user-tie</v-icon> Resumen por Vendedor ({{ sellers.length }})
            </v-tab>
            <v-tab class="font-weight-bold text-uppercase tracking-wide">
              <v-icon left small>fas fa-building</v-icon> Resumen por Cliente ({{ customers.length }})
            </v-tab>
            <v-tab class="font-weight-bold text-uppercase tracking-wide">
              <v-icon left small>fas fa-list-check</v-icon> Detalle de OTs Pendientes ({{ workOrders.length }})
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab" class="pa-4 pa-md-6">
            <!-- Tab 1: Resumen por Vendedor -->
            <v-tab-item>
              <v-row class="mb-4" align="center" justify="space-between">
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="searchSeller"
                    prepend-inner-icon="search"
                    label="Buscar vendedor..."
                    dense
                    outlined
                    hide-details
                    clearable
                    class="rounded-lg"
                  />
                </v-col>
                <v-col cols="12" sm="6" class="text-sm-right caption grey--text font-weight-bold">
                  {{ sellers.length }} ejecutivos con órdenes en mora
                </v-col>
              </v-row>

              <v-data-table
                :headers="headersSeller"
                :items="sellers"
                :search="searchSeller"
                :loading="loading"
                loading-text="Cargando resumen de ejecutivos..."
                no-data-text="No hay vendedores con OTs pendientes desde la fecha seleccionada"
                class="elevation-0"
              >
                <template v-slot:item.seller_name="{ item }">
                  <span class="font-weight-black primary--text">{{ item.seller_name }}</span>
                </template>
                <template v-slot:item.count="{ item }">
                  <v-chip x-small color="grey lighten-3" class="font-weight-black px-3">
                    {{ item.count }} OT{{ item.count !== 1 ? 's' : '' }}
                  </v-chip>
                </template>
                <template v-slot:item.avg_days="{ item }">
                  <v-chip
                    x-small
                    :color="getDaysColor(item.avg_days)"
                    dark
                    class="font-weight-black px-3"
                  >
                    {{ item.avg_days }}d
                  </v-chip>
                </template>
                <template v-slot:item.ief_score="{ item }">
                  <span :class="getIefClass(item.ief_score)" class="font-weight-black">
                    {{ item.ief_score }}%
                  </span>
                </template>
                <template v-slot:item.total_net_fmt="{ item }">
                  <span class="font-weight-black success--text text--darken-2">{{ item.total_net_fmt }}</span>
                </template>
              </v-data-table>
            </v-tab-item>

            <!-- Tab 2: Resumen por Cliente -->
            <v-tab-item>
              <v-row class="mb-4" align="center" justify="space-between">
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="searchCustomer"
                    prepend-inner-icon="search"
                    label="Buscar cliente..."
                    dense
                    outlined
                    hide-details
                    clearable
                    class="rounded-lg"
                  />
                </v-col>
                <v-col cols="12" sm="6" class="text-sm-right caption grey--text font-weight-bold">
                  {{ customers.length }} clientes con órdenes en mora
                </v-col>
              </v-row>

              <v-data-table
                :headers="headersCustomer"
                :items="customers"
                :search="searchCustomer"
                :loading="loading"
                loading-text="Cargando resumen por cliente..."
                no-data-text="No hay clientes con OTs pendientes desde la fecha seleccionada"
                class="elevation-0"
              >
                <template v-slot:item.customer_name="{ item }">
                  <span class="font-weight-black primary--text">{{ item.customer_name }}</span>
                </template>
                <template v-slot:item.count="{ item }">
                  <v-chip x-small color="grey lighten-3" class="font-weight-black px-3">
                    {{ item.count }} OT{{ item.count !== 1 ? 's' : '' }}
                  </v-chip>
                </template>
                <template v-slot:item.avg_days="{ item }">
                  <v-chip
                    x-small
                    :color="getDaysColor(item.avg_days)"
                    dark
                    class="font-weight-black px-3"
                  >
                    {{ item.avg_days }}d
                  </v-chip>
                </template>
                <template v-slot:item.ief_score="{ item }">
                  <span :class="getIefClass(item.ief_score)" class="font-weight-black">
                    {{ item.ief_score }}%
                  </span>
                </template>
                <template v-slot:item.total_net_fmt="{ item }">
                  <span class="font-weight-black success--text text--darken-2">{{ item.total_net_fmt }}</span>
                </template>
              </v-data-table>
            </v-tab-item>

            <!-- Tab 3: Detalle de OTs Pendientes -->
            <v-tab-item>
              <v-row class="mb-4" align="center" justify="space-between">
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="searchWorkOrder"
                    prepend-inner-icon="search"
                    label="Buscar OT, Cotización, Cliente o Vendedor..."
                    dense
                    outlined
                    hide-details
                    clearable
                    class="rounded-lg"
                  />
                </v-col>
                <v-col cols="12" sm="6" class="text-sm-right caption grey--text font-weight-bold">
                  {{ workOrders.length }} OTs terminadas en taller
                </v-col>
              </v-row>

              <v-data-table
                :headers="headersWorkOrder"
                :items="workOrders"
                :search="searchWorkOrder"
                :loading="loading"
                loading-text="Cargando detalle de OTs..."
                no-data-text="No hay Órdenes de Trabajo pendientes desde la fecha seleccionada"
                class="elevation-0"
              >
                <template v-slot:item.quote_id="{ item }">
                  <span class="grey--text font-weight-bold">#{{ item.quote_id || '-' }}</span>
                </template>
                <template v-slot:item.work_order_id="{ item }">
                  <v-chip small color="primary lighten-5" class="primary--text font-weight-black">
                    OT-{{ item.work_order_id }}
                  </v-chip>
                </template>
                <template v-slot:item.customer="{ item }">
                  <span class="font-weight-bold">{{ item.customer || 'Sin Cliente' }}</span>
                </template>
                <template v-slot:item.seller_name="{ item }">
                  <span class="caption font-weight-bold grey--text text--darken-2">{{ item.seller_name || 'Sin Vendedor' }}</span>
                </template>
                <template v-slot:item.days_pending="{ item }">
                  <v-chip
                    x-small
                    :color="getDaysColor(item.days_pending)"
                    dark
                    class="font-weight-black px-3"
                  >
                    {{ item.days_pending }}d
                  </v-chip>
                </template>
                <template v-slot:item.net_amount_fmt="{ item }">
                  <span class="font-weight-black success--text text--darken-2">{{ item.net_amount_fmt }}</span>
                </template>
              </v-data-table>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </template>
    </div>
  </v-container>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { generateInvoicingDashboardPdf } from '@/doc_generators/invoicingDashboardReport'

export default {
  name: 'InvoicingDashboard',
  data() {
    return {
      loading: false,
      downloading: false,
      downloadingPdf: false,
      activeTab: 0,
      activePreset: '90_days',
      presets: [
        { id: '30_days', label: '30 días', calc: () => moment().subtract(30, 'days').format('YYYY-MM-DD') },
        { id: '60_days', label: '60 días', calc: () => moment().subtract(60, 'days').format('YYYY-MM-DD') },
        { id: '90_days', label: '90 días', calc: () => moment().subtract(90, 'days').format('YYYY-MM-DD') },
        { id: '180_days', label: '180 días', calc: () => moment().subtract(180, 'days').format('YYYY-MM-DD') },
        { id: 'this_year', label: 'Este Año', calc: () => moment().startOf('year').format('YYYY-MM-DD') },
      ],
      startDate: moment().subtract(90, 'days').format('YYYY-MM-DD'),
      reportDate: '',
      timezoneName: '',
      totalCount: 0,
      totalAmount: 0,
      totalNetFmt: '',
      totalGrossFmt: '',
      avgDaysPending: 0,
      overallIef: 0,
      agingBuckets: null,
      tacticalInsight: null,
      sellers: [],
      customers: [],
      workOrders: [],
      searchSeller: '',
      searchCustomer: '',
      searchWorkOrder: '',
      headersSeller: [
        { text: 'Ejecutivo de Ventas', value: 'seller_name', align: 'left', sortable: true },
        { text: 'OTs Pendientes', value: 'count', align: 'center', sortable: true },
        { text: 'Promedio Días sin Facturar', value: 'avg_days', align: 'center', sortable: true },
        { text: 'Índice IEF (%)', value: 'ief_score', align: 'center', sortable: true },
        { text: 'Monto Neto Total', value: 'total_net_fmt', align: 'right', sortable: true },
      ],
      headersCustomer: [
        { text: 'Cliente', value: 'customer_name', align: 'left', sortable: true },
        { text: 'OTs Pendientes', value: 'count', align: 'center', sortable: true },
        { text: 'Promedio Días sin Facturar', value: 'avg_days', align: 'center', sortable: true },
        { text: 'Índice IEF (%)', value: 'ief_score', align: 'center', sortable: true },
        { text: 'Monto Neto Total', value: 'total_net_fmt', align: 'right', sortable: true },
      ],
      headersWorkOrder: [
        { text: 'Cotización', value: 'quote_id', align: 'left', sortable: true },
        { text: 'N° OT', value: 'work_order_id', align: 'left', sortable: true },
        { text: 'Cliente', value: 'customer', align: 'left', sortable: true },
        { text: 'Vendedor', value: 'seller_name', align: 'left', sortable: true },
        { text: 'Término en Planta', value: 'completed_at', align: 'center', sortable: true },
        { text: 'Días sin Facturar', value: 'days_pending', align: 'center', sortable: true },
        { text: 'Monto Neto', value: 'net_amount_fmt', align: 'right', sortable: true },
      ],
    }
  },
  computed: {
    parameters() {
      return this.$store.getters.getCompany || {}
    },
    periodLabel() {
      if (!this.startDate) return 'Histórico Completo'
      return `Terminadas desde el ${moment(this.startDate).format('DD/MM/YYYY')}`
    },
  },
  mounted() {
    this.loadDashboard()
  },
  methods: {
    applyPreset(preset) {
      this.activePreset = preset.id
      this.startDate = preset.calc()
      this.loadDashboard()
    },
    onManualDateChange() {
      this.activePreset = 'custom'
      this.loadDashboard()
    },
    async loadDashboard() {
      this.loading = true
      try {
        let url = 'sales/invoicing-dashboard'
        if (this.startDate) {
          url += `?start_date=${this.startDate}`
        }
        const response = await axios.get(url)
        const data = response.data
        this.reportDate = data.report_date
        this.timezoneName = data.timezone_name
        this.totalCount = data.total_count
        this.totalAmount = data.total_amount
        this.totalNetFmt = data.total_net_fmt
        this.totalGrossFmt = data.total_gross_fmt
        this.avgDaysPending = data.avg_days_pending
        this.overallIef = data.overall_ief
        this.agingBuckets = data.aging_buckets
        this.tacticalInsight = data.tactical_insight
        this.sellers = data.sellers || []
        this.customers = data.customers || []
        this.workOrders = data.work_orders || []
      } catch (error) {
        this.$store.dispatch('showNotification', {
          text: 'Error al cargar los datos de Capital por Facturar',
          color: 'error',
        })
      } finally {
        this.loading = false
      }
    },
    async downloadExcel() {
      this.downloading = true
      try {
        let url = 'sales/invoicing-dashboard/export-excel'
        if (this.startDate) {
          url += `?start_date=${this.startDate}`
        }
        const response = await axios.get(url, {
          responseType: 'blob',
        })
        const urlBlob = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = urlBlob
        link.setAttribute('download', `capital_por_facturar_${this.startDate || 'desde_siempre'}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
      } catch (error) {
        this.$store.dispatch('showNotification', {
          text: 'Error al descargar la planilla Excel',
          color: 'error',
        })
      } finally {
        this.downloading = false
      }
    },
    async downloadExecutivePdf() {
      this.downloadingPdf = true
      try {
        await generateInvoicingDashboardPdf({
          company: this.parameters,
          startDate: this.startDate,
          timezoneName: this.timezoneName,
          metrics: {
            totalCount: this.totalCount,
            totalAmount: this.totalAmount,
            totalNetFmt: this.totalNetFmt,
            avgDaysPending: this.avgDaysPending,
            overallIef: this.overallIef,
            agingBuckets: this.agingBuckets,
            tacticalInsight: this.tacticalInsight,
          },
          sellers: this.sellers,
          customers: this.customers,
          workOrders: this.workOrders,
        })
      } catch (error) {
        console.error('Error generando PDF de Capital Retenido:', error)
        this.$store.dispatch('showNotification', {
          text: 'Error al generar el informe ejecutivo en PDF',
          color: 'error',
        })
      } finally {
        this.downloadingPdf = false
      }
    },
    getDaysColor(days) {
      if (days <= 3) return 'success'
      if (days <= 10) return 'warning'
      return 'error'
    },
    getIefClass(score) {
      if (score >= 80) return 'success--text text--darken-2'
      if (score >= 60) return 'warning--text text--darken-3'
      return 'error--text text--darken-2'
    },
  },
}
</script>

<style scoped>
.dashboard-container {
  background-color: #f8fafc;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.border-bottom {
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.shadow-xs {
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04), 0 1px 2px rgba(15, 23, 42, 0.02);
}

.tracking-tight {
  letter-spacing: -0.02em;
}

/* Critical Pulse Ticker */
.daily-pulse-card {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.pulse-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.pulse-indicator.is-critical {
  background: #e11d48;
  box-shadow: 0 0 0 3px rgba(225, 29, 72, 0.2);
  animation: pulse-critical 2s infinite ease-in-out;
}

@keyframes pulse-critical {
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(225, 29, 72, 0.4); }
  70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(225, 29, 72, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(225, 29, 72, 0); }
}

/* Filters */
.filter-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.compact-date-input {
  max-width: 175px;
}

/* Hero KPIs */
.hero-kpi-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.hero-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.hero-kpi-card.is-primary {
  border-color: #bae6fd;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.kpi-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.kpi-value {
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
}

.unit-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #64748b;
}

.kpi-footer {
  border-top: 1px solid #f1f5f9;
}

/* Panels */
.panel-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 22px;
}

.panel-title {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.panel-sub {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-top: 2px;
}

/* Aging Distribution Stacked Track */
.margin-stacked-track {
  display: flex;
  height: 28px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
}

.stacked-segment {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  filter: brightness(1);
}

.stacked-segment:hover {
  filter: brightness(0.92);
}

.stacked-segment.is-healthy { background: #10b981; }
.stacked-segment.is-tight { background: #f59e0b; }
.stacked-segment.is-loss { background: #e11d48; }

.segment-label {
  font-size: 11px;
  font-weight: 800;
  color: white;
  padding: 0 4px;
}

/* Aging Tier Buttons */
.margin-tier-btn {
  padding: 12px 14px;
  border-radius: 10px;
}

.margin-tier-btn.is-healthy {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
}
.margin-tier-btn.is-healthy .tier-name { color: #047857; }

.margin-tier-btn.is-tight {
  background: #fffbeb;
  border: 1px solid #fde68a;
}
.margin-tier-btn.is-tight .tier-name { color: #b45309; }

.margin-tier-btn.is-loss {
  background: #fff1f2;
  border: 1px solid #fecdd3;
}
.margin-tier-btn.is-loss .tier-name { color: #be123c; }

.tier-name {
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.tier-count {
  font-size: 0.72rem;
  font-weight: 700;
  color: #64748b;
}

.tier-sales {
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  margin-top: 4px;
  letter-spacing: -0.02em;
}

.tier-share {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  margin-top: 2px;
}

.swatch {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

/* Tactical Insight Accent */
.border-top-accent {
  border-top: 4px solid #00A650 !important;
}

.lh-16 {
  line-height: 1.6 !important;
}
</style>
